// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AvalaraValidationModal from "@eisinc/App/Components/AddressSuggestionCard/AvalaraValidationModal";
import requestUpdateBillTo from "@eisinc/App/Handlers/UpdateBillTo";
import UpdateCartProperties from "@eisinc/App/Handlers/UpdateCartProperties";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@eisinc/Overrides/Components/AddressInfoDisplay";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@eisinc/Overrides/Components/CustomerAddressForm";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BillToModel,
    CountryModel,
} from "@insite/client-framework/Types/ApiModels";
import BillToSelector, { BillToSelectorStyles } from "@insite/content-library/Components/BillToSelector";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/BillingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    address: BillToModel;
    onChange: (address: BillToModel, isEdit: boolean) => void;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    extendedStyles?: BillingAddressStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
        cart,
        cartBillTo: getBillToState(state, cart?.billToId)?.value,
        // XNG-Changes
        addressFieldDataView: getAddressFieldsDataView(state),
        forceAddressValidation: getSession(state).properties.forceAddressValidation,
    };
};

const mapDispatchToProps = {
    setAddressBookOverlayIsOpen,
    // XNG-Changes
    requestUpdateBillTo,
    UpdateCartProperties,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    ThemeProps<BaseTheme>;

export interface BillingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    billToSelector?: BillToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressForm?: BillingAddressFormStyles;
    addressDisplay?: AddressInfoDisplayStyles;
    editAddressForm?: CustomerAddressFormStyles;
    // XNG-Changes
    editAddressModal?: ModalPresentationProps;
}

export const billingAddressStyles: BillingAddressStyles = {
    container: { gap: 20 },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    addressBookModal: { sizeVariant: "medium" },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: { variant: "secondary" },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
};

const BillingAddress = ({
    address,
    onChange,
    countries,
    addressFieldDisplayCollection,
    extendedStyles,
    currentUserIsGuest,
    toaster,
    isBillingAddressUpdateRequired,
    cart,
    cartBillTo,
    theme,
    setAddressBookOverlayIsOpen,
    // XNG-Changes
    requestUpdateBillTo,
    UpdateCartProperties,
    addressFieldDataView,
    forceAddressValidation,
}: Props) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<BillToModel | undefined>(undefined);
    // XNG-Changes
    const [formIsSubmitting, setFormIsSubmitting] = React.useState(false);
    const [typedAddress, setTypedAddress] = React.useState<BillToModel>();
    const avalaraValidation: any = useRef(null);

    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    const styles = mergeToNew(billingAddressStyles, extendedStyles);

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (billTo: BillToModel) => {
        customerSelectorModalCloseHandler();
        onChange(billTo, false);
    };

    const editCustomerHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BillToModel) => {
        event.preventDefault();
        setAddressBeingEdited(customer);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: BillToModel) => {
        setFormIsSubmitting(true);
        setTypedAddress(address);
        avalaraValidation.current.validate(address).then(
            (updatedAddress: BillToModel) => {
                avalaraValidation.current.countyValidation(updatedAddress, (county: any) => {
                    doEditAddressFormSubmitHandler(updatedAddress, county);
                });
            },
            (err: any) => setFormIsSubmitting(false),
        );
    };

    const doEditAddressFormSubmitHandler = (address: BillToModel, county: any) => {
        const properties = { ...address.properties, county };

        if (isRequestUpdate) {
            requestUpdateBillTo({
                billTo: { ...address, properties },
                isEdit: true,
            });
            UpdateCartProperties({ UpdateBillingAddressRequested: "true" });
        } else {
            onChange({ ...address, properties }, true);
        }

        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();

        toaster.addToast({
            messageType: "success",
            body: translate(isRequestUpdate ? "Address update requested." : "Address updated."),
        });
    };

    const requestUpdate = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(address);
    };

    const isRequestUpdate = true;

    return (
        <>
            <AvalaraValidationModal ref={avalaraValidation} address={typedAddress as any} />
            <StyledWrapper {...styles.container} data-test-selector="checkoutShipping_billingAddress">
                <StyledWrapper {...styles.headingGridItem}>
                    <Typography {...styles.headingText} as="h3">
                        {translate("Billing Address")}
                    </Typography>
                    {!currentUserIsGuest &&
                        !isRequestUpdate &&
                        cart?.status !== "QuoteProposed" &&
                        !(cart?.status === "Cart" && cart?.type === "Job") && (
                            <Link
                                {...styles.selectSavedAddressLink}
                                type="button"
                                onClick={selectAddressClickHandler}
                                data-test-selector="checkoutShipping_selectSavedAddress"
                            >
                                {translate("Select Saved Address")}
                                <VisuallyHidden>{translate("For Billing Address")}</VisuallyHidden>
                            </Link>
                        )}
                    {!currentUserIsGuest && isRequestUpdate && (
                        <Link
                            {...styles.selectSavedAddressLink}
                            type="button"
                            onClick={requestUpdate}
                            data-test-selector="checkoutShipping_selectSavedAddress"
                        >
                            {translate("Request Update")}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.addressDisplayAndFormGridItem}>
                    {currentUserIsGuest || isBillingAddressUpdateRequired ? (
                        <>
                            {addressFieldDisplayCollection && countries && (
                                <BillingAddressForm
                                    countries={countries}
                                    fieldDisplay={addressFieldDisplayCollection}
                                    extendedStyles={styles.addressForm}
                                />
                            )}
                        </>
                    ) : (
                        <AddressInfoDisplay
                            {...address}
                            state={address.state?.abbreviation}
                            country={address.country?.abbreviation}
                            extendedStyles={styles.addressDisplay}
                            {...({ addressFields: addressFieldDataView.value!.billToAddressFields } as any)}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.addressBookModal}
                headline={translate("Address Book")}
                isOpen={isCustomerSelectorModalOpen}
                handleClose={customerSelectorModalCloseHandler}
                data-test-selector="checkoutShipping_addressModal"
            >
                <BillToSelector
                    currentBillTo={cartBillTo}
                    onSelect={selectCustomerHandler}
                    onEdit={editCustomerHandler}
                    extendedStyles={styles.billToSelector}
                    {...({ addressFields: addressFieldDataView.value!.billToAddressFields } as any)}
                />
                <StyledWrapper {...styles.addressBookModalButtonsWrapper} className="modal-actions">
                    <Button
                        {...styles.addressBookModalCancelButton}
                        onClick={customerSelectorModalCloseHandler}
                        data-test-selector="addressModal_cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            {addressBeingEdited && (
                <Modal
                    {...styles.editAddressModal}
                    headline={translate(isRequestUpdate ? "Request Address Update" : "Edit Address")}
                    isOpen={isEditAddressModalOpen}
                    handleClose={editAddressModalCloseHandler}
                    data-test-selector="checkoutShipping_editAddressModal"
                >
                    <CustomerAddressForm
                        address={addressBeingEdited}
                        countries={countries}
                        addressFieldDisplayCollection={addressFieldDisplayCollection}
                        onSubmit={editAddressFormSubmitHandler}
                        saveButtonTextOverride={translate(isRequestUpdate ? "Send Request" : "Save & Apply")}
                        onCancel={editAddressModalCloseHandler}
                        extendedStyles={styles.editAddressForm}
                        forceAddressValidation={forceAddressValidation}
                        {...({ isSubmitting: formIsSubmitting } as any)}
                    />
                </Modal>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withTheme(BillingAddress)));
